/* City Uniform — product page: variant picker, add to bag, sticky bar, gallery. */
(() => {
  const theme = window.theme || { routes: {}, strings: {} };

  document.querySelectorAll('[data-product-section]').forEach((section) => {
    const sectionId = section.dataset.sectionId;
    const productUrl = section.dataset.url;
    const picker = section.querySelector('[data-variant-picker]');
    const form = section.querySelector('[data-product-form]');
    const idInput = form?.querySelector('[data-variant-id]');
    const errorEl = form?.querySelector('[data-form-error]');
    const variants = JSON.parse(picker?.querySelector('[data-variants]')?.textContent || '[]');
    const fieldsets = picker ? [...picker.querySelectorAll('fieldset[data-option-index]')] : [];
    const sizeFieldset = picker?.querySelector('[data-size-option]');
    let currentVariant = variants.find((v) => String(v.id) === idInput?.value) || null;
    let requestId = 0;

    const addButton = () => form?.querySelector('[data-add-button]');
    const setButton = (label, disabled) => {
      const btn = addButton();
      if (!btn) return;
      btn.disabled = disabled;
      const labelEl = btn.querySelector('[data-add-label]');
      if (labelEl) labelEl.textContent = label;
      const stickyBtn = section.querySelector('[data-sticky-add]');
      if (stickyBtn) stickyBtn.textContent = label;
    };
    /* Accelerated checkout buys the variant in the form, so it stays off until a full selection exists. */
    const setDynamic = (enabled) => {
      const dyn = form?.querySelector('.product__dynamic');
      if (!dyn) return;
      dyn.classList.toggle('is-disabled', !enabled);
      dyn.setAttribute('aria-disabled', String(!enabled));
      dyn.inert = !enabled;
    };
    const showError = (message) => {
      if (!errorEl) return;
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    };

    const selectedOptions = () => fieldsets.map((fs) => fs.querySelector('input:checked')?.value ?? null);
    const isComplete = () => selectedOptions().every((v) => v !== null);
    const findVariant = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i])) || null;

    /* Mark values that are sold out or do not exist with the other current selections. */
    const updateAvailability = () => {
      const opts = selectedOptions();
      fieldsets.forEach((fs, index) => {
        fs.querySelectorAll('input').forEach((input) => {
          const candidate = [...opts];
          candidate[index] = input.value;
          const others = candidate.map((v, i) => (i === index ? input.value : v));
          const match = variants.filter((v) => v.options.every((o, i) => others[i] === null || o === others[i]));
          const available = match.some((v) => v.available);
          input.classList.toggle('is-unavailable', !available);
        });
        const label = fs.querySelector('[data-selected-value]');
        if (label) label.textContent = fs.querySelector('input:checked')?.value || '';
      });
    };

    /* Re-render price / button / stock from the server so money formatting and stock stay true. */
    const renderVariant = async (variant) => {
      const id = ++requestId;
      section.querySelectorAll('[data-update]').forEach((el) => el.classList.add('is-loading'));
      try {
        const res = await fetch(`${productUrl}?variant=${variant.id}&section_id=${sectionId}`);
        const html = await res.text();
        if (id !== requestId) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        section.querySelectorAll('[data-update]').forEach((el) => {
          const fresh = doc.querySelector(`[data-update="${el.dataset.update}"]`);
          if (fresh) el.innerHTML = fresh.innerHTML;
        });
        const stickyBtn = section.querySelector('[data-sticky-add]');
        const label = addButton()?.querySelector('[data-add-label]')?.textContent.trim();
        if (stickyBtn && label) stickyBtn.textContent = label;
      } catch (e) {
        /* Keep the current markup; the hidden input already holds the right variant. */
      } finally {
        section.querySelectorAll('[data-update]').forEach((el) => el.classList.remove('is-loading'));
      }
    };

    const scrollToMedia = (variant) => {
      const mediaId = variant?.featured_media?.id;
      if (!mediaId) return;
      const item = section.querySelector(`[data-media-id="${mediaId}"]`);
      const list = section.querySelector('[data-media-list]');
      if (!item || !list) return;
      if (list.scrollWidth > list.clientWidth) {
        list.scrollTo({ left: item.offsetLeft - list.offsetLeft, behavior: 'smooth' });
      }
    };

    const onChange = () => {
      showError('');
      updateAvailability();
      if (!isComplete()) {
        currentVariant = null;
        setButton(theme.strings.chooseSize, false);
        setDynamic(false);
        return;
      }
      const variant = findVariant(selectedOptions());
      currentVariant = variant;
      if (!variant) {
        setButton(theme.strings.unavailable, true);
        setDynamic(false);
        return;
      }
      setDynamic(variant.available);
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
      setButton(variant.available ? theme.strings.addToBag : theme.strings.soldOut, !variant.available);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      scrollToMedia(variant);
      renderVariant(variant);
    };

    if (picker) {
      picker.addEventListener('change', onChange);
      /* Ask for a size first unless a variant was chosen through the URL. */
      const hasVariantParam = new URL(window.location.href).searchParams.has('variant');
      const sizeInputs = sizeFieldset ? sizeFieldset.querySelectorAll('input') : [];
      if (!hasVariantParam && sizeInputs.length > 1 && variants.some((v) => v.available)) {
        sizeInputs.forEach((input) => { input.checked = false; });
        currentVariant = null;
        setButton(theme.strings.chooseSize, false);
        setDynamic(false);
      }
      updateAvailability();
    }

    /* ---------- Add to bag ---------- */
    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (picker && !isComplete()) {
        showError(theme.strings.chooseSize);
        (sizeFieldset || fieldsets[0])?.querySelector('input')?.focus();
        return;
      }
      if (picker && !currentVariant) return;
      const btn = addButton();
      const spinner = btn?.querySelector('[data-spinner]');
      btn?.setAttribute('aria-busy', 'true');
      if (btn) btn.disabled = true;
      if (spinner) spinner.hidden = false;
      showError('');
      try {
        const body = new FormData(form);
        const res = await fetch(`${theme.routes.cartAdd}.js`, { method: 'POST', body, headers: { Accept: 'application/json' } });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || theme.strings.cartError);
        await theme.refreshBagCount?.(); if (window.CartDrawer && typeof window.CartDrawer.open === 'function') window.CartDrawer.open();
        theme.showBagNotice?.(`${theme.strings.addedToBag}: ${data.product_title}${data.variant_title ? ` — ${data.variant_title}` : ''}`);
      } catch (error) {
        showError(error.message || theme.strings.cartError);
      } finally {
        btn?.removeAttribute('aria-busy');
        if (btn) btn.disabled = currentVariant ? !currentVariant.available : false;
        if (spinner) spinner.hidden = true;
      }
    });

    /* ---------- Sticky bar (mobile) ---------- */
    const bar = section.querySelector('[data-sticky-bar]');
    const buyArea = section.querySelector('.product__buy');
    if (bar && buyArea && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(([entry]) => {
        bar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      });
      io.observe(buyArea);
      bar.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
        if (picker && !isComplete()) {
          (sizeFieldset || picker).scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => (sizeFieldset || fieldsets[0])?.querySelector('input')?.focus({ preventScroll: true }), 400);
          showError(theme.strings.chooseSize);
          return;
        }
        form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    }

    /* ---------- Gallery counter (mobile slider) ---------- */
    const list = section.querySelector('[data-media-list]');
    const counter = section.querySelector('[data-media-index]');
    if (list && counter) {
      list.addEventListener('scroll', theme.debounce ? theme.debounce(() => {
        const index = Math.round(list.scrollLeft / list.clientWidth) + 1;
        counter.textContent = String(Math.min(index, list.children.length));
      }, 60) : null, { passive: true });
    }
  });

  /* ---------- 3D models: load Shopify's viewer only when needed ---------- */
  if (document.querySelector('model-viewer') && window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (errors) => {
          if (errors) return;
          document.querySelectorAll('model-viewer').forEach((el) => new window.Shopify.ModelViewerUI(el));
        }
      },
      { name: 'shopify-xr', version: '1.0' }
    ]);
  }

  /* Pause other videos when one plays. */
  document.addEventListener('play', (event) => {
    document.querySelectorAll('.product-media video').forEach((v) => { if (v !== event.target) v.pause(); });
  }, true);
})();
